/**
 * Feature-owned client-safe display contract for the `RatingsByMovesCount`
 * N/K ratio shown alongside the existing task retry count.
 *
 * This module must remain free of React, database, PGN, server, and generic
 * model imports so it can be consumed by browser components and written to
 * the client payload.
 */

export type RatingByMovesCountDisplay = Readonly<{
  wrongMoveCount: number;
  totalMoveCount: number;
}>;

export type RatingByMovesCountDisplayCarrier = Readonly<{
  ratingByMovesCountDisplay?: RatingByMovesCountDisplay;
}>;

export type RatingByMovesCountDisplayMapCarrier = Readonly<{
  ratingByMovesCountDisplayByProblemId: Readonly<
    Record<number, RatingByMovesCountDisplay>
  >;
}>;

export type RatingByMovesCountDisplayMap = Readonly<
  Record<number, RatingByMovesCountDisplay>
>;

type RatingByMovesCountDisplayResponse = RatingByMovesCountDisplayCarrier &
  Readonly<{
    problemId?: number;
    updatedTaskState?: Readonly<{ problemId?: number }> | null;
  }>;

const isValidPositiveInteger = (value: number): boolean =>
  Number.isFinite(value) && Number.isInteger(value) && value > 0;

const isValidMetric = (
  metric: RatingByMovesCountDisplay | null | undefined,
): metric is RatingByMovesCountDisplay => {
  if (!metric) return false;
  const { wrongMoveCount, totalMoveCount } = metric;
  return (
    Number.isFinite(wrongMoveCount) &&
    Number.isInteger(wrongMoveCount) &&
    wrongMoveCount >= 0 &&
    Number.isFinite(totalMoveCount) &&
    Number.isInteger(totalMoveCount) &&
    totalMoveCount > 0 &&
    wrongMoveCount <= totalMoveCount
  );
};

export function mergeRatingByMovesCountDisplayResponse(
  previous: RatingByMovesCountDisplayMap,
  response: RatingByMovesCountDisplayResponse,
): RatingByMovesCountDisplayMap {
  const problemId = response.updatedTaskState?.problemId ?? response.problemId;
  if (
    problemId === undefined ||
    !isValidPositiveInteger(problemId) ||
    !isValidMetric(response.ratingByMovesCountDisplay)
  ) {
    return previous;
  }

  return {
    ...previous,
    [problemId]: response.ratingByMovesCountDisplay,
  };
}

export function removeRatingByMovesCountDisplay(
  previous: RatingByMovesCountDisplayMap,
  problemId: number,
): RatingByMovesCountDisplayMap {
  if (!(problemId in previous)) return previous;
  const next = { ...previous };
  delete next[problemId];
  return next;
}

/**
 * Produces the combined display text for an existing retry count `R` and the
 * optional current-retry wrong-required-move ratio `N/K`.
 *
 * Contract:
 * - R=0, N=0        -> null (no indicator)
 * - R=0, N>0        -> "N/K"
 * - R>0, N=0        -> "R"
 * - R>0, N>0        -> "R · N/K" (middle dot with surrounding spaces)
 *
 * An invalid or non-positive retry is treated as zero. An invalid metric is
 * ignored, in which case the behavior collapses to the existing retry-only
 * rules (positive R renders as R, otherwise null).
 */
export function formatRatingByMovesCountRetryDisplay(
  retryCount: number | null | undefined,
  metric: RatingByMovesCountDisplay | null | undefined,
): string | null {
  const retryCandidate = retryCount ?? 0;
  const retry = isValidPositiveInteger(retryCandidate) ? retryCandidate : 0;
  if (isValidMetric(metric) && metric.wrongMoveCount > 0) {
    const ratio = `${metric.wrongMoveCount}/${metric.totalMoveCount}`;
    if (retry > 0) {
      return `${retry} · ${ratio}`;
    }
    return ratio;
  }

  if (retry > 0) {
    return `${retry}`;
  }

  return null;
}
